import i18n, { t } from '@if/core/i18n'
import {
  CreditDecision,
  EBeneficiaryType,
  ECreditApplicationStatus,
  EFinancingProgram,
  EGender,
  EHomeStatus,
  EJobType,
  ELanguage,
  ELoanPurpose,
  EOriginSystemId,
  EOtherIncomeType,
  EPaymentPlan,
  EProgressionStatus,
  getEHomeStatusList,
  getELanguageList,
} from '@if/core/types'
import * as yup from '@if/core/yup'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { AddressSchema, BaseAddressSchema } from './AddressSchema'
import { Contract } from './Contract'
import { NormCreditDecision } from './CreditDecision'
import { ElectronicSignature } from './ElectronicSignature'
import { FilteredWorksheet } from './FilteredWorksheet'
import { PrequalificationDecision } from './PrequalificationDecision'
import { RequiredDocument } from './RequiredDocument'

export const expensesSchema = yup.object({
  financingProgramId: yup
    .mixed<EFinancingProgram>()
    .nullable()
    .default('' as EFinancingProgram),

   monthlyInstallmentLoanPayment: yup.number().min(0).nullable(),
   monthlyStudentLoanPayment: yup.number().min(0).nullable(),
   monthlyLineOfCreditPayment: yup.number().min(0).nullable(),
   monthlyCreditCardsPayment: yup.number().min(0).nullable(),
   monthlyPaydayLoanPayment: yup.number().min(0).nullable(),
   monthlyPersonalLoanPayment: yup.number().min(0).nullable(),
  monthlyOtherLoanPayment: yup.number().min(0).nullable(),

  monthlyOtherExpensePayment: yup.number().positive().nullable(),
  otherExpensePaymentDescription: yup
    .string()
    .nullable()
    .max(50)
    .when('monthlyOtherExpensePayment', (val) => {
      return val && (val as unknown as number) > 0 ? yup.string().required().default('') : yup.string().notRequired()
    }),

  homeFeeTypeId: yup
    .mixed<EHomeStatus>()
    .oneOf([undefined, ...getEHomeStatusList()])
    .default(null)
    .nullable()
    .when('financingProgramId', {
      is: EFinancingProgram.Personal,
      then: (schema) => schema.required(),
    }),

  houseMarketValue: yup
    .double()
    .min(1, () => t('common.errors.positive'))
    .notRequired()
    .nullable(),

  rentMonthly: yup.double(),
  mortgageMonthly: yup.double(),

  housingMonthly: yup.integer().when('homeFeeTypeId', {
    is: (value: EHomeStatus) => value === EHomeStatus.Renter,
    then: (schema) => schema.required().min(1),
    otherwise: (schema) => schema.notRequired().positive(),
  }),

  landlord: yup.string().nullable().notRequired().default(''),

  monthlyOwnerInsuranceFee: yup.double().min(0),
  monthlyMortgageInsuranceFee: yup.double().min(0),
  monthlyMunicipalAndSchoolTaxPayment: yup.double().min(0),
  monthlyCondominiumFee: yup.double().min(0),
  monthlyPublicServiceFee: yup.double().min(0),
  monthlyRrspRefund: yup.double().min(0),
  monthlyOtherHomeFee: yup.double().min(0),

  isCompleted: yup.bool().default(false).nullable(),
})

export type ApplicantExpenses = yup.InferType<typeof expensesSchema>

export const ExpensesSchema = expensesSchema.when((value: ApplicantExpenses[], schema) => {
  if (value[0]?.houseMarketValue || value[0]?.housingMonthly) {
    schema.fields.homeFeeTypeId = yup.mixed<EHomeStatus>().oneOf(getEHomeStatusList()).required()
  }
  return schema
})

const ApplicantAddressSchema = () =>
  AddressSchema.shape({
    years: yup.integer().required().min(0),
    months: yup.number().nullable().optional(),
  })

export const JobSchema = () =>
  yup.object({
    id: yup.string().default(''),
    employerName: yup.string().required().default(''),
    jobTitle: yup.string().required().default(''),
    jobType: yup.numberEnum<EJobType>().required().default(null),
    annualSalary: yup
      .double()
      .required()
      .integer(() => t('common.errors.integer'))
      .min(1, () => t('common.errors.positive'))
      .default(null),
    employerPhone: yup
      .string()
      .nullable()
      .when('jobType', {
        is: (jobType: EJobType) => jobType === EJobType.FullTime || jobType === EJobType.PartTime,
        then: (schema) => schema.required().validCanadianPhone(),
        otherwise: (schema) => schema.validCanadianPhone(),
      }),
    employerPhoneExt: yup.string().validPhoneExtension().nullable(),
    address: BaseAddressSchema.nullable(),
    years: yup.integer().required().min(0),
    months: yup.number().nullable().optional(),
    verificationContactName: yup.string().nullable(),
    verificationContactRole: yup.string().nullable(),
    verificationContactPhone: yup.string().nullable(),
    isIncomeLocked: yup.bool().default(false),
  })

export type ApplicantJob = yup.InferType<ReturnType<typeof JobSchema>>

export const OtherIncomeSchema = yup.object({
  id: yup.string().default(''),
  typeId: yup.integer().required(),
  description: yup
    .string()
    .nullable()
    .when('typeId', (value) => {
      return value[0] === EOtherIncomeType.Other ? yup.string().required() : yup.string().notRequired()
    }),
  annualAmount: yup
    .integer()
    .required()
    .when('typeId', (value) => {
      return value[0] === EOtherIncomeType.IncomeAdjustmentToMatchStatements
        ? yup.integer().required()
        : yup.integer().required().positive().min(0)
    }),
  isIncomeLocked: yup.bool().default(false),
})
export type ApplicantOtherIncome = yup.InferType<typeof OtherIncomeSchema>

export interface IncomeVerification {
  thirdPartyFlowCompleted: boolean
  incomeMismatch: boolean
  nameMismatch: boolean
  hasPendingFraudAlert: boolean
  allIncomeTasksConfirmed: boolean
  hasIncomeServiceAdjustment: boolean
  hasSkippedIncomeValidation: boolean
  thirdPartyIncome: number
  declaredIncome: number
  requiredExternalStep: RequiredExternalStep
}

export interface RequiredExternalStep {
  applicantType: string
  status: string
}

const draftApplicantSchema = () => ({
  id: yup.string().nullable().default(null), // le default null est nécessaire pour le createEntityAdapter
  financingProgramId: yup.mixed<EFinancingProgram>(),
  middleName: yup.string().validCharacters().max(50).default('').nullable(),
  relationWithApplicant: yup.string().when('isPrimaryApplicant', (value) => {
    return !value[0]
      ? yup.string().nullable().required().default('')
      : yup.string().notRequired().nullable().default('')
  }),
  hasLegalName: yup.bool().required().default(false),
  firstName: yup.string().validCharacters().max(50).required().default(''),
  lastName: yup.string().when('hasLegalName', {
    is: true,
    then: (schema) => schema.oneOf(['']).max(0).default(''),
    otherwise: (schema) => schema.validCharacters().max(50).required().default(''),
  }),
  birthDate: yup.string().default('').validDate().validAgeRequirement(yup.ref('currentAddress.stateIso')),
  sin: yup.string().validSIN().nullable().default(''),
  languageId: yup
    .numberEnum<ELanguage>(getELanguageList())
    .nonNullable()
    .default(() => (i18n.resolvedLanguage === 'fr' ? ELanguage.French : ELanguage.English))
    .required(),
  genderId: yup.numberEnum<EGender>().nullable().default(null),
  maritalStatusId: yup.integer().nullable().default(null),
  homePhone: yup.string().default('').validCanadianPhone().nullable(),
  cellPhone: yup.string().default('').validCanadianPhone().nullable(),
  email: yup.string().notRequired().email().max(60).default(''),
  // Incomes
  currentJobs: yup.array(JobSchema()).default([]),
  previousJobs: yup.array(JobSchema()).default([]),
  otherIncomes: yup.array(OtherIncomeSchema).default([]),
  // address
  currentAddress: ApplicantAddressSchema(),
  expenses: ExpensesSchema.notRequired().default(ExpensesSchema.getDefault()),
  isPrimaryApplicant: yup.bool().required().default(true),
  isApplicantNewToCredit: yup.bool().required().default(false),
  hasSin: yup.bool().required().default(false),
})

export const DraftApplicantSchema = () =>
  yup.object(draftApplicantSchema())

const fullApplicantSchema = () => {
  const schema = { ...draftApplicantSchema() }
  schema.birthDate = schema.birthDate.required()
  schema.genderId = schema.genderId.required()
  schema.languageId = schema.languageId.required()
  schema.maritalStatusId = schema.maritalStatusId.required()
  schema.cellPhone = schema.cellPhone.required()
  schema.email = schema.email.required()
  return schema
}

const FullApplicantSchema = () =>
  yup.object(fullApplicantSchema())

export interface CreditAlertsDto extends Record<string, boolean> {
  hasNameMismatch: boolean
  hasDoBMismatch: boolean
  hasPreviousApplication: boolean
  hasCreditLocked: boolean
  hasInvalidCity: boolean
  hasGeneralCreditReportError: boolean
  isBanned: boolean
}

export type ApplicantDto = yup.InferType<ReturnType<typeof DraftApplicantSchema>> & {
  incomeVerification?: IncomeVerification
  identityVerification?: RequiredExternalStep | null
  hardHitReportReceivedOn?: Date
  creditAlerts?: CreditAlertsDto
  hasCreditReport?: boolean
  isApplicantNewToCredit: boolean
  hasSin: boolean
}
export const buildDraftCreditApplicationSchemaObject = () => {
  const draftCreditApplicationSchema = {
    id: yup.string().default(''), // le default null est nécessaire pour le createEntityAdapter
    financingProgramId: yup.mixed<EFinancingProgram>(),
    loanPurposeId: yup
      .string()
      .nullable()
      .when('financingProgramId', ([financingProgramId], schema) => {
        const config = programConfigByFinancingProgramId(financingProgramId as EFinancingProgram)
        if (config?.loanPurposeIdIsRequired ?? true) {
          return schema.required()
        }
        return yup.string().notRequired().nullable()
      })
      .default(null),
    requestedLoanAmount: yup.double().when('financingProgramId', (financingProgramId, schema) => {
      const value = financingProgramId[0] as EFinancingProgram
      const config = programConfigByFinancingProgramId(value)
      if (config?.requestedLoanAmountIsRequired ?? true) {
        return schema.required().min(yup.ref('$minLoanValue')).max(yup.ref('$maxLoanValue'))
      }

      return yup.double()
    }),
    merchantId: yup.string().required().default(''),
    status: yup.mixed<ECreditApplicationStatus>().required().default(ECreditApplicationStatus.Draft),
    applicant: DraftApplicantSchema().required(),
    coApplicant: DraftApplicantSchema().nullable().default(null),
    editLocked: yup.bool().default(false),
    correspondanceLanguageId: yup.number(),
    versionTag: yup.string(),
    beneficiaryTypeId: yup
      .integer()
      .nullable()
      .when('financingProgramId', ([financingProgramId], schema) => {
        const config = programConfigByFinancingProgramId(financingProgramId as EFinancingProgram)
        if (config?.beneficiaryIsRequired ?? true) {
          return schema
            .required()
            .oneOf([0, EBeneficiaryType.Applicant, EBeneficiaryType.Coapplicant, EBeneficiaryType.Other])
            .default(EBeneficiaryType.Applicant)
        }
        return yup.integer().nullable()
      })
      .default(null),
    otherBeneficiaryFirstName: yup
      .string()
      .trim()
      .when('loanPurposeId', (loanPurposeId, schema) => {
        if (loanPurposeId[0] === ELoanPurpose.Veterinary || loanPurposeId[0] === ELoanPurpose.GoodsAndServices) {
          return schema.notRequired().nullable()
        }
        return schema.when('beneficiaryTypeId', (value) => {
          return value[0] === EBeneficiaryType.Other ? yup.string().required() : yup.string().notRequired().nullable()
        })
      }),

    otherBeneficiaryLastName: yup
      .string()
      .trim()
      .when('loanPurposeId', (loanPurposeId, schema) => {
        if (loanPurposeId[0] === ELoanPurpose.GoodsAndServices) {
          return schema.notRequired().nullable()
        }
        return schema.when('beneficiaryTypeId', (value) => {
          return value[0] === EBeneficiaryType.Other ? yup.string().required() : yup.string().notRequired().nullable()
        })
      }),
    originSystemId: yup.mixed<EOriginSystemId>().required().default(EOriginSystemId.MerchantDashboard),
    consentSoftHit: yup.bool().required().default(false),
    consentHardHit: yup.bool().required().default(false),
    isSinglePayment: yup.bool().default(false),
    isInvisalign: yup.bool().default(false),
    merchantReferenceCode: yup.string().notRequired().max(20),
    merchantPaymentPlanId: yup
      .mixed<EPaymentPlan>()
      .nullable()
      .default(null),
    prequalPromoCode: yup
      .string()
      .nullable()
      .default(null),
  }
  return yup.object(draftCreditApplicationSchema).test(
    'test-coappSameMobileError',
    () => t('common.errors.coappSameMobileError'),
    function distinctCellphoneTest(value) {
      const { createError } = this
      if (value?.coApplicant !== null) {
        const coappCellPhone = value.coApplicant.cellPhone
        const appCellphone = value.applicant.cellPhone
        if (appCellphone && coappCellPhone) {
          return (
            appCellphone !== coappCellPhone ||
            createError({ path: 'coApplicant.cellPhone', message: t('common.errors.coappSameMobileError') })
          )
        }
      }
      return true
    },
  )
}
export const buildDraftCreditApplicationSchema = () => {
  return buildDraftCreditApplicationSchemaObject()
}
export const DraftCreditApplicationSchema = () =>
  buildDraftCreditApplicationSchema()

export const buildFullCreditApplicationSchema = () => {
  const schema = buildDraftCreditApplicationSchemaObject().shape({
    merchantId: yup.string().required(),
    applicant: FullApplicantSchema().required(),
    coApplicant: FullApplicantSchema().nullable().default(null),
  })
  return schema
}

export const FullCreditApplicationSchema = () =>
  buildFullCreditApplicationSchema()

export type DraftCreditApplicationDto = yup.InferType<ReturnType<typeof DraftCreditApplicationSchema>>

type EditCreditApplicationDto = yup.InferType<ReturnType<typeof FullCreditApplicationSchema>>

export type CreditApplication = EditCreditApplicationDto & {
  applicant: ApplicantDto
  coApplicant: ApplicantDto | null
  currentRevisionId: string
  referenceNumber: number
  parentReferenceNumber: number | null
  parentCreditApplicationId: string | null
  normsCreditDecision: NormCreditDecision | null
  finalCreditDecision: CreditDecision & { isHardDecline: boolean }
  createdOn: Date
  updatedOn: Date
  expiresOn: Date
  deliveryOn: string
  createdByUserFullName: string
  correspondanceLanguageId: number
  cvtNumber: number | null
  loanId: number | null
  loanPurposeId: ELoanPurpose
  prequalificationDecision: PrequalificationDecision | null
  merchantPaymentPlanId: EPaymentPlan
  prequalPromoCode: string | null
  allIncomesConfirmed: boolean
  requiredDocuments: RequiredDocument[]
  loanCreated: boolean
  beneficiaryTypeId: number
  otherBeneficiaryFirstName: string
  otherBeneficiaryLastName: string
  consentSoftHit: boolean
  consentHardHit: boolean
  isSinglePayment: boolean
  isInvisalign: boolean
  electronicSignatures: ElectronicSignature[]
  financingProgramId: EFinancingProgram
  worksheet: FilteredWorksheet | null
  contract: Contract
  hasTasksBeforeCvt: boolean
  availableCreditAmount: number
  hasOngoingElectronicSignature: boolean
  progressionStatus: EProgressionStatus
  merchantReferenceCode: string | null
  merchantDocumentUploadRequestSentOn: Date | null
}
const editApplicantContactInfoDtoSchema = {
  id: yup.string().default('').required(),
  cellPhone: yup.string().nullable().default('').validCanadianPhone(),
  email: yup.string().nullable().email().max(60).default(''),
  applicantType: yup.string().nullable().default(null),
  financingProgramId: yup.mixed<EFinancingProgram>().required(),
  languageId: yup
    .numberEnum<ELanguage>(getELanguageList())
    .nullable()
    .default(() => (i18n.resolvedLanguage === 'fr' ? ELanguage.French : ELanguage.English))
    .required(),
}

export const EditApplicantContactInfoDtoSchema = yup.object(editApplicantContactInfoDtoSchema)

export type EditApplicantContactInfoDto = yup.InferType<typeof EditApplicantContactInfoDtoSchema>
